<p-toast
  position="bottom-center"
  key="confirm"
  (onClose)="onReject()"
  [baseZIndex]="5000"
>
  <ng-template let-message pTemplate="message">
    <div class="flex flex-column align-items-start" style="flex: 1">
      <div class="font-medium text-lg my-3 text-900">
        {{ message.summary }}
      </div>
      <div
        style="font-family: math"
        class="font-medium text-lg my-3 text-red-600"
      >
        {{ message.detail }}
      </div>
    </div>
  </ng-template>
</p-toast>

<div class="confirmation-overlay" *ngIf="showProfileDialog" [@overlayAnimation]>
  <div class="profile-dialog" [@dialogAnimation]>
    <div class="dialog-header">
      <div class="header-content">
        <i class="pi" [ngClass]="{
          'pi-user-edit text-blue-600': !showChangePassword,
          'pi-key text-blue-600': showChangePassword
        }"></i>
        <h2 class="dialog-title">{{ showChangePassword ? 'Change Password' : 'Edit Profile' }}</h2>
      </div>
    </div>

    <div class="dialog-content">
      <div class="profile-image-section" *ngIf="!showChangePassword">
        <div class="profile-image-container">
          <img [src]="profilePicUrl || defaultImageUrl" (error)="onImageError($event)"
            class="profile-image rounded-circle object-cover" />
          <input type="file" #fileInput (change)="onImageSelected($event)" accept="image/*" style="display: none;">
        </div>
      </div>

      <div class="form-section">
        <!-- Error Alert for Profile -->
        <div *ngIf="profileError && !showChangePassword" class="error-alert">
          <i class="pi pi-exclamation-triangle"></i>
          <span>{{ profileError }}</span>
          <button class="close-alert" (click)="clearProfileError()">
            <i class="pi pi-times"></i>
          </button>
        </div>

        <!-- Error Alert for Password -->
        <div *ngIf="passwordError && showChangePassword" class="error-alert">
          <i class="pi pi-exclamation-triangle"></i>
          <span>{{ passwordError }}</span>
          <button class="close-alert" (click)="clearPasswordError()">
            <i class="pi pi-times"></i>
          </button>
        </div>

        <!-- Success Alert for Password -->
        <div *ngIf="passwordSuccess && showChangePassword" class="success-alert">
          <i class="pi pi-check-circle"></i>
          <span>{{ passwordSuccess }}</span>
          <button class="close-alert" (click)="clearPasswordSuccess()">
            <i class="pi pi-times"></i>
          </button>
        </div>

        <div class="email-display">
          <p class="email-text">{{ userEmail }}</p>
        </div>
        <div class="form-group" *ngIf="!showChangePassword">
          <label for="nameInput">Full Name</label>
          <input id="nameInput" type="text" [(ngModel)]="editableName" class="form-control"
            placeholder="Enter your full name">
        </div>

        <!-- Password Section -->
        <div class="password-section" [class.password-mode]="showChangePassword">
          <!-- Change Password Button - Show when NOT in password change mode -->
          <button *ngIf="!showChangePassword" class="change-password-btn" (click)="enablePasswordChange()">
            <i class="pi pi-key"></i>
            Change Password
          </button>

          <!-- Back to Profile Button - Show when IN password change mode -->
          <button *ngIf="showChangePassword" class="back-to-profile-btn" (click)="disablePasswordChange()">
            <i class="pi pi-arrow-left"></i>
            Back to Profile
          </button>

          <!-- Password Form - Show when IN password change mode -->
          <form *ngIf="showChangePassword" [formGroup]="passwordForm" (ngSubmit)="onChangePassword()"
            class="password-form">

            <!-- Current Password -->
            <div class="form-group">
              <label for="currentPassword">Current Password</label>
              <input id="currentPassword" type="password" formControlName="currentPassword" class="form-control"
                placeholder="Enter current password"
                [class.is-invalid]="currentPasswordControl.invalid && currentPasswordControl.touched">

              <!-- Validation Messages -->
              <div *ngIf="currentPasswordControl.invalid && currentPasswordControl.touched" class="error-message">
                <div *ngIf="currentPasswordControl.errors?.['required']">
                  Current password is required
                </div>
                <div *ngIf="currentPasswordControl.errors?.['minlength']">
                  Password must be at least 6 characters long
                </div>
              </div>
            </div>

            <!-- New Password -->
            <div class="form-group">
              <label for="newPassword">New Password</label>
              <input id="newPassword" type="password" formControlName="newPassword" class="form-control"
                placeholder="Enter new password"
                [class.is-invalid]="newPasswordControl.invalid && newPasswordControl.touched">

              <!-- Validation Messages -->
              <div *ngIf="newPasswordControl.invalid && newPasswordControl.touched" class="error-message">
                <div *ngIf="newPasswordControl.errors?.['required']">
                  New password is required
                </div>
                <div *ngIf="newPasswordControl.errors?.['minlength']">
                  Password must be at least 6 characters long
                </div>
              </div>
            </div>

            <!-- Confirm New Password -->
            <div class="form-group">
              <label for="confirmPassword">Confirm New Password</label>
              <input id="confirmPassword" type="password" formControlName="confirmPassword" class="form-control"
                placeholder="Confirm new password"
                [class.is-invalid]="confirmPasswordControl.invalid && confirmPasswordControl.touched">

              <!-- Validation Messages -->
              <div *ngIf="confirmPasswordControl.invalid && confirmPasswordControl.touched" class="error-message">
                <div *ngIf="confirmPasswordControl.errors?.['required']">
                  Please confirm your new password
                </div>
              </div>

              <!-- Password Mismatch Error -->
              <div *ngIf="passwordForm.errors?.['passwordMismatch'] && confirmPasswordControl.touched"
                class="error-message">
                Passwords do not match
              </div>
            </div>

            <!-- Common Action Buttons for Password Form -->
            <div class="common-actions">
              <button type="button" class="common-btn common-btn-secondary" 
                      (click)="disablePasswordChange()" 
                      [disabled]="isProcessing">
                Cancel
              </button>
              <button type="submit" class="common-btn common-btn-primary" 
                      [disabled]="passwordForm.invalid || isProcessing">
                <span class="button-content">
                  <i class="pi pi-spin pi-spinner" *ngIf="isProcessing"></i>
                  <span *ngIf="!isProcessing">Change Password</span>
                  <span *ngIf="isProcessing">Changing Password...</span>
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Common Action Buttons for Profile Form -->
    <div *ngIf="!showChangePassword" class="common-actions">
      <button class="common-btn common-btn-secondary" 
              (click)="closeProfileDialog()" 
              [disabled]="isProcessing">
        Cancel
      </button>
      <button class="common-btn common-btn-primary" 
              (click)="saveProfileChanges()" 
              [disabled]="isProcessing || !isFormValid()">
        <span class="button-content">
          <i class="pi pi-spin pi-spinner" *ngIf="isProcessing"></i>
          <span *ngIf="!isProcessing">Save Changes</span>
          <span *ngIf="isProcessing">Saving...</span>
        </span>
      </button>
    </div>
  </div>
</div>

<!-- Add this to your template after the existing profile button -->


<mat-toolbar class="topbar" [ngClass]="{ 'no-border': isModulePage }" [style.background-color]="'white'"
  [style.color]="isModulePage ? 'black' : 'black'">
  <button mat-icon-button (click)="toggleMobileNav.emit()" *ngIf="showToggle">
    <i-tabler name="menu-2" class="icon-20 d-flex"></i-tabler>
  </button>

  <img class="responsive-image" [src]="loginPath" alt="Description of the image" width="100" height="50"
    style="cursor:pointer" />

  <!-- <h2
    *ngIf="isModulePage"
    style="flex: 20; text-align: center"
    class="responseText"
  >
    Module
  </h2> -->
  <span class="flex-1-auto"></span>


  <span class="font-bold profile-name">
    {{ userDetails?.name }}
  </span>

  <!-- Update your existing profile button -->
  <button mat-icon-button aria-label="Notifications" class="m-l-10 profile-button" (click)="openProfileDialog()">
    <img [src]="profilePicUrl || defaultImageUrl" (error)="onImageError($event)" class="rounded-circle object-cover"
      width="35" />
  </button>
  <!-- <mat-menu #profilemenu="matMenu" xPosition="before" class="topbar-dd cardWithShadow">
    <button mat-menu-item [routerLink]="['/module/profile/activities']">
      <mat-icon class="d-flex align-items-center">
        <i-tabler name="list-check" class="icon-18 d-flex"></i-tabler> </mat-icon>My Activity
    </button>

    <div class="p-x-12 m-t-12">
      <a (click)="logout()" mat-flat-button color="primary" class="w-100">Logout</a>
    </div>
  </mat-menu> -->


  <!-- <span class="font-bold profile-name">
    {{ profileName }}
  </span>
  <button mat-icon-button [matMenuTriggerFor]="profilemenu" aria-label="Notifications" class="m-l-10">
    <img [src]="profilePicUrl || defaultImageUrl" (error)="onImageError($event)" class="rounded-circle object-cover"
      width="35" />
  </button>
  <mat-menu #profilemenu="matMenu" xPosition="before" class="topbar-dd cardWithShadow">
    <button mat-menu-item [routerLink]="['/module/profile/activities']">
      <mat-icon class="d-flex align-items-center">
        <i-tabler name="list-check" class="icon-18 d-flex"></i-tabler> </mat-icon>My Activity
    </button>

    <div class="p-x-12 m-t-12">
      <a (click)="logout()" mat-flat-button color="primary" class="w-100">Logout</a>
    </div>
  </mat-menu> -->
</mat-toolbar>